import React, { useState } from 'react';
import { Building2, ChevronDown, Check, AlertCircle } from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';

/**
 * RiadSwitcher - Component for switching between riads (tenants)
 * 
 * Displays current riad info (logo/name) and dropdown for multi-riad users
 * Single-riad users only see the riad display without dropdown
 */
export const RiadSwitcher = ({ currentRiad, accesses, onSwitch }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Determine if user has multiple riads
  const hasMultipleRiads = accesses && accesses.length > 1;
  
  // Get current riad display info
  const riadName = currentRiad?.name || 'Mon Riad';
  const riadLogo = currentRiad?.logo;
  
  // Handle riad switch
  const handleSwitch = async (riadId) => {
    if (riadId === currentRiad?.id) {
      setIsOpen(false);
      return;
    }
    
    // Check if target riad is blocked
    const targetRiad = accesses.find(a => a.riad_id === riadId);
    if (targetRiad?.subscription_status === 'EXPIRED' || 
        targetRiad?.subscription_status === 'SUSPENDED' ||
        targetRiad?.subscription_status === 'CANCELLED') {
      toast.error(`Ce riad a un abonnement ${targetRiad.subscription_status === 'EXPIRED' ? 'expiré' : 'suspendu'}`);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await api.post(`/auth/switch-riad/${riadId}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setIsOpen(false);
        
        // CRITICAL: Update localStorage BEFORE reload to ensure correct X-Riad-ID header
        const storedUserStr = localStorage.getItem('user');
        if (storedUserStr) {
          try {
            const storedUser = JSON.parse(storedUserStr);
            storedUser.current_riad_id = riadId;
            storedUser.current_riad = response.data.riad;
            // Update accesses to mark new current
            if (storedUser.accesses) {
              storedUser.accesses = storedUser.accesses.map(a => ({
                ...a,
                is_current: a.riad_id === riadId
              }));
            }
            localStorage.setItem('user', JSON.stringify(storedUser));
          } catch (e) {
            console.error('Error updating localStorage user:', e);
          }
        }
        
        // Trigger callback or full reload
        if (onSwitch) {
          onSwitch(response.data.riad);
        } else {
          // Full page reload to clear all state
          setTimeout(() => {
            window.location.reload();
          }, 300);
        }
      }
    } catch (error) {
      const errorData = error.response?.data;
      
      if (errorData?.blocked) {
        toast.error(errorData.error, {
          description: 'Contactez l\'administrateur pour réactiver l\'accès.'
        });
      } else {
        toast.error(errorData?.error || 'Erreur lors du changement de riad');
      }
      setIsLoading(false);
    }
  };
  
  // Render riad logo or placeholder
  const RiadLogo = ({ logo, name, size = 'normal' }) => {
    const sizeClasses = size === 'small' ? 'w-8 h-8' : 'w-10 h-10';
    const iconSize = size === 'small' ? 'w-4 h-4' : 'w-5 h-5';
    
    // Build logo URL - can be a filename from uploads or a full URL
    const logoUrl = logo 
      ? (logo.startsWith('http') ? logo : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${logo}`)
      : null;
    
    if (logoUrl) {
      return (
        <img 
          src={logoUrl} 
          alt={name}
          className={`${sizeClasses} rounded-lg object-cover bg-white`}
          onError={(e) => {
            // Hide broken images and show placeholder instead
            e.target.style.display = 'none';
            e.target.nextSibling?.classList.remove('hidden');
          }}
        />
      );
    }
    
    return (
      <div className={`${sizeClasses} rounded-lg bg-primary/10 flex items-center justify-center`}>
        <Building2 className={`${iconSize} text-primary`} />
      </div>
    );
  };
  
  // Get status indicator color
  const getStatusColor = (subscriptionStatus) => {
    switch (subscriptionStatus) {
      case 'ACTIVE':
      case 'LIFETIME':
        return 'bg-green-500';
      case 'TRIAL':
        return 'bg-blue-500';
      case 'EXPIRED':
      case 'SUSPENDED':
      case 'CANCELLED':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };
  
  return (
    <div className="relative" data-testid="riad-switcher">
      {/* Current Riad Display */}
      <button
        onClick={() => hasMultipleRiads && setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`
          w-full flex items-center gap-3 p-3 rounded-lg transition-colors
          ${hasMultipleRiads ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'}
          ${isLoading ? 'opacity-50' : ''}
        `}
        data-testid="riad-switcher-trigger"
      >
        <RiadLogo logo={riadLogo} name={riadName} />
        
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-semibold text-gray-900 truncate" data-testid="current-riad-name">
            {riadName}
          </p>
          {currentRiad?.subscription_status && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(currentRiad.subscription_status)}`} />
              <span className="text-xs text-muted-foreground capitalize">
                {currentRiad.subscription_status === 'ACTIVE' ? 'Actif' : 
                 currentRiad.subscription_status === 'TRIAL' ? 'Essai' :
                 currentRiad.subscription_status === 'EXPIRED' ? 'Expiré' :
                 currentRiad.subscription_status}
              </span>
            </div>
          )}
        </div>
        
        {hasMultipleRiads && (
          <ChevronDown 
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>
      
      {/* Dropdown Menu */}
      {isOpen && hasMultipleRiads && (
        <>
          {/* Overlay to close */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div 
            className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-64 overflow-y-auto"
            data-testid="riad-switcher-dropdown"
          >
            <div className="p-1">
              <p className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vos établissements
              </p>
              
              {accesses.map((access) => {
                const isCurrent = access.riad_id === currentRiad?.id;
                const isBlocked = ['EXPIRED', 'SUSPENDED', 'CANCELLED'].includes(access.subscription_status);
                
                return (
                  <button
                    key={access.riad_id}
                    onClick={() => handleSwitch(access.riad_id)}
                    disabled={isLoading || isBlocked}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors
                      ${isCurrent ? 'bg-primary/5' : 'hover:bg-gray-50'}
                      ${isBlocked ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                    data-testid={`riad-option-${access.riad_id}`}
                  >
                    <RiadLogo logo={access.riad_logo} name={access.riad_name} size="small" />
                    
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {access.riad_name}
                      </p>
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(access.subscription_status)}`} />
                        <span className="text-xs text-muted-foreground">
                          {access.role === 'SUPER_ADMIN' ? 'Super Admin' : 
                           access.role === 'PROPRIETAIRE' ? 'Propriétaire' :
                           access.role === 'manager' ? 'Gestionnaire' :
                           access.role === 'staff' ? 'Personnel' :
                           access.role}
                        </span>
                      </div>
                    </div>
                    
                    {isCurrent && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                    
                    {isBlocked && (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RiadSwitcher;
